import { useCallback, useEffect, useState } from 'react'
import {
  attendanceCreateAbsenceReason,
  attendanceDeleteAbsenceReason,
  attendanceLoadAbsenceReasons,
  attendanceUpdateAbsenceReason,
  type AttendanceSimpleLookup,
} from '../../lib/apiPhpAdapter'
import './AttendanceOrgAdmin.css'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  /** Kompaktní vzhled uvnitř Personalista panelu */
  embedded?: boolean
  /** Po změně katalogu (např. obnovit kódy v přehledu) */
  onChanged?: () => void | Promise<void>
}

type FormState = {
  code: string
  name: string
  wage_code: string
  allow_stravenka: boolean
  active: boolean
}

const emptyForm = (): FormState => ({
  code: '',
  name: '',
  wage_code: '',
  allow_stravenka: false,
  active: true,
})

function formFromItem(it: AttendanceSimpleLookup): FormState {
  return {
    code: it.code || '',
    name: it.name || '',
    wage_code: it.wage_code || '',
    allow_stravenka: Boolean(it.allow_stravenka),
    active: it.active === undefined || it.active === null ? true : Boolean(Number(it.active)),
  }
}

export function AttendanceAbsenceReasonsPanel({ showAlert, embedded = false, onChanged }: Props) {
  const [items, setItems] = useState<AttendanceSimpleLookup[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<FormState>(emptyForm)
  const [editItem, setEditItem] = useState<AttendanceSimpleLookup | null>(null)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)
  const [deleteItem, setDeleteItem] = useState<AttendanceSimpleLookup | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const list = await attendanceLoadAbsenceReasons()
      setItems(Array.isArray(list) ? list : [])
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst nepřítomnosti', 'error')
    } finally {
      setLoading(false)
    }
  }, [showAlert])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    if (!createOpen && !editItem && !deleteItem) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        setCreateOpen(false)
        setEditItem(null)
        setDeleteItem(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [createOpen, editItem, deleteItem, busy])

  const openCreate = () => {
    setEditItem(null)
    setDeleteItem(null)
    setCreateForm(emptyForm())
    setCreateOpen(true)
  }

  const openEdit = (it: AttendanceSimpleLookup) => {
    setCreateOpen(false)
    setDeleteItem(null)
    setEditItem(it)
    setEditForm(formFromItem(it))
  }

  const openDelete = (it: AttendanceSimpleLookup) => {
    setCreateOpen(false)
    setEditItem(null)
    setDeleteItem(it)
  }

  const onCreate = async () => {
    const name = createForm.name.trim()
    if (!name) {
      showAlert('Název je povinný', 'warning')
      return
    }
    setBusy(true)
    try {
      await attendanceCreateAbsenceReason({
        name,
        code: createForm.code.trim() || undefined,
        wage_code: createForm.wage_code.trim() || undefined,
        allow_stravenka: createForm.allow_stravenka,
      })
      showAlert('Nepřítomnost vytvořena', 'success')
      setCreateForm(emptyForm())
      setCreateOpen(false)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onSaveEdit = async () => {
    if (!editItem) return
    const name = editForm.name.trim()
    if (!name) {
      showAlert('Název je povinný', 'warning')
      return
    }
    setBusy(true)
    try {
      await attendanceUpdateAbsenceReason(editItem.id, {
        name,
        code: editForm.code.trim() || null,
        wage_code: editForm.wage_code.trim() || null,
        allow_stravenka: editForm.allow_stravenka,
        active: editForm.active,
      })
      showAlert('Nepřítomnost upravena', 'success')
      setEditItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const onConfirmDelete = async () => {
    if (!deleteItem) return
    setBusy(true)
    try {
      await attendanceDeleteAbsenceReason(deleteItem.id)
      showAlert('Smazáno', 'success')
      setDeleteItem(null)
      await reload()
      await onChanged?.()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const Wrapper = embedded ? 'div' : 'section'
  const wrapperClass = embedded ? 'att-cat' : 'attendance-panel att-cat'

  return (
    <Wrapper className={wrapperClass}>
      {!embedded ? <h2>Karta nepřítomností</h2> : null}
      <p className="att-cat__intro">
        Správa důvodů nepřítomnosti (kód, název, mzdový kód, stravenka).
      </p>

      <div className="att-org__add att-cat__add">
        <button type="button" className="system-button" disabled={busy} onClick={openCreate}>
          Přidat nepřítomnost
        </button>
      </div>

      {loading ? <p className={embedded ? 'att-prehled__muted' : 'attendance-muted'}>Načítání…</p> : null}

      <div className="att-cat__wrap">
        <table className="att-cat__table">
          <thead>
            <tr>
              <th className="att-cat__col-code">Kód</th>
              <th className="att-cat__col-name">Název</th>
              <th className="att-cat__col-wage">Mzd. kód</th>
              <th className="att-cat__col-narrow">Strav.</th>
              <th className="att-cat__col-narrow">Stav</th>
              <th className="att-cat__col-actions att-cat__col-fill" aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const isActive = it.active === undefined || it.active === null ? true : Boolean(Number(it.active))
              return (
                <tr key={it.id} className={isActive ? undefined : 'att-cat__row--inactive'}>
                  <td className="att-cat__col-code" title={it.code || undefined}>
                    {it.code || '—'}
                  </td>
                  <td className="att-cat__col-name" title={it.name}>
                    {it.name}
                  </td>
                  <td className="att-cat__col-wage" title={it.wage_code || undefined}>
                    {it.wage_code || '—'}
                  </td>
                  <td className="att-cat__col-narrow">{it.allow_stravenka ? 'ano' : '—'}</td>
                  <td className="att-cat__col-narrow">{isActive ? 'aktivní' : 'neaktivní'}</td>
                  <td className="att-cat__col-actions">
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy}
                      onClick={() => openEdit(it)}
                    >
                      Upravit
                    </button>
                    <button
                      type="button"
                      className="system-button att-cat__btn att-cat__btn--danger"
                      disabled={busy}
                      onClick={() => openDelete(it)}
                    >
                      Smazat
                    </button>
                  </td>
                </tr>
              )
            })}
            {!loading && items.length === 0 ? (
              <tr>
                <td colSpan={6} className="att-cat__empty">
                  Žádné důvody nepřítomnosti.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setCreateOpen(false)
          }}
        >
          <div
            className="att-abs-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-abs-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-abs-create-title">Nová nepřítomnost</h3>
            <div className="att-abs-modal__fields">
              <label>
                Kód
                <input
                  value={createForm.code}
                  disabled={busy}
                  onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value }))}
                />
              </label>
              <label>
                Název
                <input
                  value={createForm.name}
                  disabled={busy}
                  autoFocus
                  onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label>
                Mzdový kód
                <input
                  value={createForm.wage_code}
                  disabled={busy}
                  onChange={(e) => setCreateForm((f) => ({ ...f, wage_code: e.target.value }))}
                />
              </label>
              <label className="att-abs-modal__check">
                <input
                  type="checkbox"
                  checked={createForm.allow_stravenka}
                  disabled={busy}
                  onChange={(e) => setCreateForm((f) => ({ ...f, allow_stravenka: e.target.checked }))}
                />
                povolí stravenku
              </label>
            </div>
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !createForm.name.trim()}
                onClick={() => void onCreate()}
              >
                {busy ? 'Ukládám…' : 'Vytvořit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setCreateOpen(false)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setEditItem(null)
          }}
        >
          <div
            className="att-abs-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-abs-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-abs-edit-title">Upravit nepřítomnost</h3>
            <div className="att-abs-modal__fields">
              <label>
                Kód
                <input
                  value={editForm.code}
                  disabled={busy}
                  onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))}
                />
              </label>
              <label>
                Název
                <input
                  value={editForm.name}
                  disabled={busy}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                />
              </label>
              <label>
                Mzdový kód
                <input
                  value={editForm.wage_code}
                  disabled={busy}
                  onChange={(e) => setEditForm((f) => ({ ...f, wage_code: e.target.value }))}
                />
              </label>
              <label className="att-abs-modal__check">
                <input
                  type="checkbox"
                  checked={editForm.allow_stravenka}
                  disabled={busy}
                  onChange={(e) => setEditForm((f) => ({ ...f, allow_stravenka: e.target.checked }))}
                />
                povolí stravenku
              </label>
              <label className="att-abs-modal__check">
                <input
                  type="checkbox"
                  checked={editForm.active}
                  disabled={busy}
                  onChange={(e) => setEditForm((f) => ({ ...f, active: e.target.checked }))}
                />
                aktivní
              </label>
            </div>
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || !editForm.name.trim()}
                onClick={() => void onSaveEdit()}
              >
                {busy ? 'Ukládám…' : 'Uložit'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setEditItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteItem ? (
        <div
          className="att-abs-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!busy) setDeleteItem(null)
          }}
        >
          <div
            className="att-abs-modal att-abs-modal--danger"
            role="dialog"
            aria-modal
            aria-labelledby="att-abs-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-abs-delete-title">Smazat nepřítomnost?</h3>
            <p className="att-abs-modal__warn">
              Opravdu chcete smazat důvod{' '}
              <strong>
                {deleteItem.code ? `${deleteItem.code} — ` : ''}
                {deleteItem.name}
              </strong>
              ?
            </p>
            <p className="att-abs-modal__warn-sub">
              Tato akce je trvalá. Pokud je důvod použit v přehledech docházky, může to ovlivnit
              historická data.
            </p>
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                style={{ background: '#ef4444' }}
                disabled={busy}
                onClick={() => void onConfirmDelete()}
              >
                {busy ? 'Mazání…' : 'Ano, smazat'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setDeleteItem(null)}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Wrapper>
  )
}
